import { useState } from "react";
import {
  useApiCredentials,
  useCreateApiCredential,
  useRotateApiCredential,
  useRevokeApiCredential,
  useDeleteApiCredential,
  CREDENTIAL_SCOPES,
  READ_ONLY_SCOPES,
  ALL_SCOPES,
  type ApiCredential,
  type ApiCredentialSecret,
} from "../../api/apiCredentials";
import { useGroups } from "../../api/groups";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { Plus, Copy, Ban, RefreshCw, Trash2 } from "lucide-react";

export function ApiCredentialsPage() {
  const { data, isLoading, isError } = useApiCredentials();
  const { data: groupsData } = useGroups({ counts: false });
  const createCred = useCreateApiCredential();
  const rotateCred = useRotateApiCredential();
  const revokeCred = useRevokeApiCredential();
  const deleteCred = useDeleteApiCredential();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  // The freshly-issued secret — shown exactly once in a modal after create/rotate.
  const [secret, setSecret] = useState<ApiCredentialSecret | null>(null);
  const [confirm, setConfirm] = useState<null | { action: "revoke" | "rotate" | "delete"; cred: ApiCredential }>(null);

  const creds = data?.data ?? [];
  const groups = groupsData?.data ?? [];

  function showFlash(message: string) {
    setFlash(message);
    setTimeout(() => setFlash(null), 3000);
  }

  function copy(value: string, label: string) {
    navigator.clipboard.writeText(value);
    showFlash(`Copied ${label} to clipboard.`);
  }

  function toggleScope(value: string) {
    setScopes((s) => (s.includes(value) ? s.filter((x) => x !== value) : [...s, value]));
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createCred.mutate(
      { name, groupId, scopes },
      {
        onSuccess: (res) => {
          setShowCreate(false);
          setName("");
          setGroupId("");
          setScopes([]);
          setSecret(res.data);
        },
        onError: () => showFlash("Couldn't create the credential — see console for details."),
      },
    );
  }

  async function runConfirmedAction() {
    if (!confirm) return;
    const { action, cred } = confirm;
    setConfirm(null);
    try {
      if (action === "revoke") {
        await revokeCred.mutateAsync(cred.id);
        showFlash(`Revoked "${cred.name}".`);
      } else if (action === "delete") {
        await deleteCred.mutateAsync(cred.id);
        showFlash(`Deleted "${cred.name}".`);
      } else {
        const res = await rotateCred.mutateAsync(cred.id);
        setSecret(res.data);
      }
    } catch {
      showFlash("Operation failed — see console for details.");
    }
  }

  const columns = [
    { key: "name", header: "Name", render: (row: ApiCredential) => <span className="font-medium">{row.name}</span> },
    {
      key: "clientId",
      header: "Client ID",
      render: (row: ApiCredential) => (
        <div className="flex items-center gap-1.5">
          <code className="text-xs">{row.clientId}</code>
          <button
            onClick={(e) => { e.stopPropagation(); copy(row.clientId, "client ID"); }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-gold-400"
            aria-label="Copy client ID"
          >
            <Copy size={12} />
          </button>
        </div>
      ),
    },
    { key: "groupId", header: "Group", render: (row: ApiCredential) => <span className="font-mono text-xs">{row.groupId}</span> },
    {
      key: "scopes",
      header: "Scopes",
      render: (row: ApiCredential) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((s) => (
            <code key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] dark:bg-riot-navy-700 dark:text-riot-navy-100">
              {s.replace("riot-fota/", "")}
            </code>
          ))}
        </div>
      ),
    },
    { key: "status", header: "Status", render: (row: ApiCredential) => <StatusBadge status={row.status} /> },
    // "Last used" is intentionally not shown: the backend does not yet stamp a
    // last-used timestamp, so it would read "Never" for every credential and
    // mislead the revoke decision. Restore this column once a writer exists
    // (tracked in go-live #5).
    { key: "createdAt", header: "Created", render: (row: ApiCredential) => new Date(row.createdAt).toLocaleDateString() },
    {
      key: "actions",
      header: "Actions",
      render: (row: ApiCredential) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {row.status === "active" && (
            <>
              <button
                onClick={() => setConfirm({ action: "rotate", cred: row })}
                className="flex items-center gap-1 rounded border border-blue-300 px-2 py-1 text-[11px] font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-800/60 dark:text-blue-300 dark:hover:bg-blue-950/40"
                title="Rotate — issue a new secret; the old one stops working immediately"
              >
                <RefreshCw size={11} /> Rotate
              </button>
              <button
                onClick={() => setConfirm({ action: "revoke", cred: row })}
                className="flex items-center gap-1 rounded border border-orange-300 px-2 py-1 text-[11px] font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-950/40"
                title="Revoke — disable this credential (keeps the row)"
              >
                <Ban size={11} /> Revoke
              </button>
            </>
          )}
          <button
            onClick={() => setConfirm({ action: "delete", cred: row })}
            className="flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40"
            title="Delete — permanently remove this credential"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      ),
    },
  ];

  const canCreate = name.trim() !== "" && groupId !== "" && scopes.length > 0;

  return (
    <div>
      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {flash}
        </div>
      )}

      {secret && (
        <SecretModal
          secret={secret}
          onClose={() => {
            // Drop the plaintext secret from component state AND the mutation
            // caches so it doesn't linger in memory after the modal closes.
            setSecret(null);
            createCred.reset();
            rotateCred.reset();
          }}
          onCopy={copy}
        />
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div role="dialog" aria-modal="true" className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {confirm.action === "rotate"
                ? "Rotate credential secret"
                : confirm.action === "delete"
                  ? "Delete credential"
                  : "Revoke credential"}
            </h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">
              {confirm.action === "rotate"
                ? `Rotating "${confirm.cred.name}" issues a new client ID + secret and immediately stops the current secret working. Any integration using it will break until updated.`
                : confirm.action === "delete"
                  ? `Permanently deleting "${confirm.cred.name}" removes it and its Cognito client for good. Any integration still using it stops working. The credential's history stays in the audit log — this can't be undone.`
                  : `Revoking "${confirm.cred.name}" disables it within minutes. Any integration using it will start getting 403s. It stays in the list as "revoked" (delete it later to tidy up).`}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={runConfirmedAction}
                className={
                  confirm.action === "rotate"
                    ? "rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                    : confirm.action === "revoke"
                      ? "rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
                      : "rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                }
              >
                {confirm.action === "rotate" ? "Rotate secret" : confirm.action === "delete" ? "Delete permanently" : "Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Create API credential</h3>
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (e.g. Fleet config sync)"
              required
              maxLength={120}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            />
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              required
              aria-label="Group"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            >
              <option value="">Select a group…</option>
              {groups.map((g) => (
                <option key={g.groupId} value={g.groupId}>
                  {g.name}
                </option>
              ))}
            </select>
            <fieldset className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <legend className="text-xs text-gray-600 dark:text-riot-navy-200">Scopes</legend>
                <span className="text-[11px] text-gray-400 dark:text-riot-navy-400">— quick set:</span>
                <button
                  type="button"
                  onClick={() => setScopes(READ_ONLY_SCOPES)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                >
                  Read-only
                </button>
                <button
                  type="button"
                  onClick={() => setScopes(ALL_SCOPES)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700"
                >
                  Full access
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {CREDENTIAL_SCOPES.map((s) => (
                  <label key={s.value} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-riot-navy-100">
                    <input type="checkbox" checked={scopes.includes(s.value)} onChange={() => toggleScope(s.value)} />
                    {s.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={createCred.isPending || !canCreate}
                className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {createCred.isPending ? "Creating..." : "Create"}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading API credentials...</div>
      ) : isError ? (
        // Never show a failed load as "no credentials": an admin would think
        // their integrations' credentials were gone.
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          Couldn&apos;t load API credentials. Refresh to try again.
        </p>
      ) : creds.length === 0 && !showCreate ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center dark:border-riot-navy-700">
          <p className="text-sm text-gray-500 dark:text-riot-navy-300">No API credentials yet.</p>
          <p className="mt-1 text-xs text-gray-400 dark:text-riot-navy-400">
            Create one to let a server-to-server integration call the API on your behalf.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500"
          >
            <Plus size={14} /> Create credential
          </button>
        </div>
      ) : (
        <DataTable
          title="API Credentials"
          totalCount={creds.length}
          columns={columns}
          data={creds}
          keyExtractor={(c) => c.id}
          filters={{
            searchPlaceholder: "Search credentials",
            searchFields: [
              { value: "name", label: "Name" },
              { value: "clientId", label: "Client ID" },
              { value: "groupId", label: "Group" },
            ],
            statusOptions: [
              { value: "active", label: "Active" },
              { value: "revoked", label: "Revoked" },
            ],
          }}
          actions={
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
            >
              <Plus size={14} /> Create credential
            </button>
          }
        />
      )}
    </div>
  );
}

// SecretModal shows a freshly-issued credential's secret ONCE. The clientSecret
// is never retrievable again, so the copy affordances + warning are load-bearing.
function SecretModal({
  secret,
  onClose,
  onCopy,
}: {
  secret: ApiCredentialSecret;
  onClose: () => void;
  onCopy: (value: string, label: string) => void;
}) {
  const rows: { label: string; value?: string }[] = [
    { label: "Client ID", value: secret.clientId },
    { label: "Client secret", value: secret.clientSecret },
    { label: "Token endpoint", value: secret.tokenEndpoint },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" aria-modal="true" className="w-[min(560px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Save your credential now</h3>
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          This is the <strong>only time</strong> the client secret is shown. Store it somewhere safe — you can't retrieve it later
          (you'd have to rotate for a new one).
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {rows.filter((r) => r.value).map((r) => (
            <div key={r.label}>
              <div className="text-[11px] font-medium text-gray-500 dark:text-riot-navy-300">{r.label}</div>
              <div className="mt-0.5 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 dark:border-riot-navy-600 dark:bg-riot-navy-900">
                <code className="flex-1 break-all text-xs text-gray-800 dark:text-riot-navy-100">{r.value}</code>
                <button
                  onClick={() => onCopy(r.value!, r.label.toLowerCase())}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-gold-400"
                  aria-label={`Copy ${r.label.toLowerCase()}`}
                >
                  <Copy size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md bg-riot-navy-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-riot-navy-800 dark:bg-riot-gold-400 dark:text-riot-navy-900 dark:hover:bg-riot-gold-500"
          >
            I've saved it
          </button>
        </div>
      </div>
    </div>
  );
}
